import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Tabs,
  Tab,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Avatar,
  LinearProgress,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line
} from 'recharts';
import {
  TrendingUp,
  People,
  Timer,
  Visibility,
  CalendarToday,
  DateRange,
  Refresh,
  Analytics as AnalyticsIcon,
  Smartphone,
  Computer
} from '@mui/icons-material';
import { getUserAnalytics, getUserAnalyticsRange, getUserAnalyticsStats, getAllAnalytics } from '../../Utils/apiServices';
import './Analytics.css';

const Analytics = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [period, setPeriod] = useState('all');
  const [userId, setUserId] = useState('476');
  const [startDate, setStartDate] = useState('2025-07-17');
  const [endDate, setEndDate] = useState('2025-07-20');
  const [userAnalytics, setUserAnalytics] = useState([]);
  const [userStats, setUserStats] = useState(null);
  const [allAnalytics, setAllAnalytics] = useState([]);
  const [filteredAnalytics, setFilteredAnalytics] = useState([]);
  const [selectedUserFilter, setSelectedUserFilter] = useState('all');
  const [loading, setLoading] = useState(false);

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D'];

  useEffect(() => {
    fetchData();
  }, [period, userId, activeTab]);

  useEffect(() => {
    // Reset user filter when period changes
    if (activeTab === 2) {
      setSelectedUserFilter('all');
      setFilteredAnalytics(allAnalytics);
    }
  }, [period, activeTab, allAnalytics]);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (activeTab === 0) {
        // User Analytics
        const [analyticsRes, statsRes] = await Promise.all([
          getUserAnalytics(userId, period),
          getUserAnalyticsStats(userId)
        ]);
        setUserAnalytics(analyticsRes.data.data || []);
        setUserStats(statsRes.data.data || null);
      } else if (activeTab === 1) {
        // Date Range Analytics
        const rangeRes = await getUserAnalyticsRange(userId, startDate, endDate);
        setUserAnalytics(rangeRes.data.data || []);
      } else {
        // All Analytics
        const allRes = await getAllAnalytics(period);
        const analyticsData = allRes.data.data || [];
        setAllAnalytics(analyticsData);
        setFilteredAnalytics(analyticsData);
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    if (newValue === 1) {
      fetchData();
    }
  };

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}h ${minutes}m ${secs}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  };

  const getDeviceIcon = (platform) => {
    return platform === 'Android' ? <Smartphone /> : <Computer />;
  };

  const handleUserFilterChange = (event) => {
    const selectedUser = event.target.value;
    setSelectedUserFilter(selectedUser);
    
    if (selectedUser === 'all') {
      setFilteredAnalytics(allAnalytics);
    } else {
      const filtered = allAnalytics.filter(item => item.user_id === selectedUser);
      setFilteredAnalytics(filtered);
    }
  };

  const getUniqueUsers = () => {
    const users = [...new Set(allAnalytics.map(item => item.user_id))];
    return users.sort();
  };

  const renderUserAnalytics = () => (
    <Box>
      {userStats && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: 'primary.main', color: 'white' }}>
              <CardContent>
                <Box display="flex" alignItems="center">
                  <Timer sx={{ fontSize: 40, mr: 2 }} />
                  <Box>
                    <Typography variant="h4">{formatTime(userStats.total_time)}</Typography>
                    <Typography variant="body2">Total Time</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: 'secondary.main', color: 'white' }}>
              <CardContent>
                <Box display="flex" alignItems="center">
                  <People sx={{ fontSize: 40, mr: 2 }} />
                  <Box>
                    <Typography variant="h4">{userStats.total_sessions}</Typography>
                    <Typography variant="body2">Total Sessions</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: 'success.main', color: 'white' }}>
              <CardContent>
                <Box display="flex" alignItems="center">
                  <Visibility sx={{ fontSize: 40, mr: 2 }} />
                  <Box>
                    <Typography variant="h4">{userStats.unique_pages}</Typography>
                    <Typography variant="body2">Unique Pages</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: 'warning.main', color: 'white' }}>
              <CardContent>
                <Box display="flex" alignItems="center">
                  <TrendingUp sx={{ fontSize: 40, mr: 2 }} />
                  <Box>
                    <Typography variant="h6">{userStats.most_visited_page?.page_name}</Typography>
                    <Typography variant="body2">Most Visited</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Page Visit Distribution</Typography>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={userAnalytics}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ page_name, percent }) => `${page_name} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="total_seconds"
                    nameKey="page_name"
                  >
                    {userAnalytics.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <RechartsTooltip />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Time Spent by Page</Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={userAnalytics}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="page_name" />
                  <YAxis />
                  <RechartsTooltip formatter={(value) => formatTime(value)} />
                  <Bar dataKey="total_seconds" fill="#8884d8" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Detailed Analytics</Typography>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Page Name</TableCell>
                  <TableCell>Total Time</TableCell>
                  <TableCell>Visit Count</TableCell>
                  <TableCell>Device Info</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {userAnalytics.map((row, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <Chip 
                          label={row.page_name} 
                          color="primary" 
                          size="small"
                          sx={{ mr: 1 }}
                        />
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <Typography variant="body2" sx={{ mr: 1 }}>
                          {formatTime(row.total_seconds)}
                        </Typography>
                        <LinearProgress 
                          variant="determinate" 
                          value={userAnalytics.length > 0 ? (row.total_seconds / Math.max(...userAnalytics.map(a => a.total_seconds))) * 100 : 0}
                          sx={{ width: 60, height: 6, borderRadius: 3 }}
                        />
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={row.visit_count} 
                        color="secondary" 
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <Tooltip title={`${row.device_info.platform} - ${row.device_info.model}`}>
                          <IconButton size="small">
                            {getDeviceIcon(row.device_info.platform)}
                          </IconButton>
                        </Tooltip>
                        <Typography variant="caption" sx={{ ml: 1 }}>
                          v{row.device_info.appVersion}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Tooltip title="View Details">
                        <IconButton size="small" color="primary">
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );

  const renderDateRangeAnalytics = () => (
    <Box>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Date Range Analytics</Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="User ID"
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                type="date"
                label="Start Date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                type="date"
                label="End Date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <Button
                variant="contained"
                onClick={fetchData}
                startIcon={<Refresh />}
                fullWidth
              >
                Fetch Data
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      {renderUserAnalytics()}
    </Box>
  );

  const renderAllAnalytics = () => (
    <Box>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>All Users Analytics</Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Period</InputLabel>
                <Select
                  value={period}
                  label="Period"
                  onChange={(e) => setPeriod(e.target.value)}
                >
                  <MenuItem value="all">All Time</MenuItem>
                  <MenuItem value="daily">Daily</MenuItem>
                  <MenuItem value="weekly">Weekly</MenuItem>
                  <MenuItem value="monthly">Monthly</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Filter by User</InputLabel>
                <Select
                  value={selectedUserFilter}
                  label="Filter by User"
                  onChange={handleUserFilterChange}
                >
                  <MenuItem value="all">All Users</MenuItem>
                  {getUniqueUsers().map((user) => (
                    <MenuItem key={user} value={user}>
                      {user === 'guest_user' ? 'Guest User' : `User ${user}`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Box sx={{ textAlign: 'center', p: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Showing {filteredAnalytics.length} of {allAnalytics.length} records
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Button
                variant="contained"
                onClick={fetchData}
                startIcon={<Refresh />}
                fullWidth
              >
                Refresh Data
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>All Users Analytics Data</Typography>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>User ID</TableCell>
                  <TableCell>Page Name</TableCell>
                  <TableCell>Total Time</TableCell>
                  <TableCell>Visit Count</TableCell>
                  <TableCell>Device Info</TableCell>
                  <TableCell>Date/Period</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredAnalytics.map((row, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Chip 
                        label={row.user_id} 
                        color={row.user_id === 'guest_user' ? 'warning' : 'primary'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={row.page_name} 
                        color="secondary" 
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{formatTime(row.total_seconds)}</TableCell>
                    <TableCell>{row.visit_count}</TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <Tooltip title={`${row.device_info.platform} - ${row.device_info.model}`}>
                          <IconButton size="small">
                            {getDeviceIcon(row.device_info.platform)}
                          </IconButton>
                        </Tooltip>
                        <Typography variant="caption" sx={{ ml: 1 }}>
                          v{row.device_info.appVersion}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {row.date && (
                        <Chip 
                          label={row.date} 
                          color="info" 
                          size="small"
                          icon={<CalendarToday />}
                        />
                      )}
                      {row.week_start && (
                        <Chip 
                          label={`Week: ${new Date(row.week_start).toLocaleDateString()}`} 
                          color="info" 
                          size="small"
                          icon={<DateRange />}
                        />
                      )}
                      {row.month_start && (
                        <Chip 
                          label={`Month: ${new Date(row.month_start).toLocaleDateString()}`} 
                          color="info" 
                          size="small"
                          icon={<DateRange />}
                        />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );

  return (
    <Box className="analytics-container">
      <Box
        sx={{
          mb: 3,
          p: 2.5,
          borderRadius: 3,
          background: 'linear-gradient(135deg, #22c55e 0%, #0ea5e9 50%, #6366f1 100%)',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: '0 8px 24px rgba(34,197,94,0.3)',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: -40,
            right: -40,
            width: 150,
            height: 150,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.1)',
          },
        }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.3 }}>📈 Analytics Dashboard</Typography>
          <Typography variant="body2" sx={{ opacity: 0.85 }}>Track user behaviour, session data and engagement metrics</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AnalyticsIcon sx={{ fontSize: 40, opacity: 0.3 }} />
        </Box>
      </Box>

      <Card sx={{ mb: 3 }} className="analytics-card">
        <CardContent>
          <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 2 }}>
            <Tab label="User Analytics" />
            <Tab label="Date Range Analytics" />
            <Tab label="All Users Analytics" />
          </Tabs>

          {activeTab === 0 && (
            <Box>
              <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="User ID"
                    value={userId}
                    onChange={(e) => setUserId(e.target.value)}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Period</InputLabel>
                    <Select
                      value={period}
                      label="Period"
                      onChange={(e) => setPeriod(e.target.value)}
                    >
                      <MenuItem value="all">All Time</MenuItem>
                      <MenuItem value="daily">Daily</MenuItem>
                      <MenuItem value="weekly">Weekly</MenuItem>
                      <MenuItem value="monthly">Monthly</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Button
                    variant="contained"
                    onClick={fetchData}
                    startIcon={<Refresh />}
                    fullWidth
                  >
                    Refresh
                  </Button>
                </Grid>
              </Grid>
            </Box>
          )}
        </CardContent>
      </Card>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {activeTab === 0 && renderUserAnalytics()}
      {activeTab === 1 && renderDateRangeAnalytics()}
      {activeTab === 2 && renderAllAnalytics()}
    </Box>
  );
};

export default Analytics; 